import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit,
  AfterViewChecked, ElementRef, QueryList, ViewChildren } from '@angular/core';
import { NotificationService } from './notification.service';
import { NotificationEntry } from './notification.model';
import { AuthService } from '../../auth/auth.service';
import { Subject, Observable, EMPTY, interval } from 'rxjs';
import { WebsocketService } from '../../shared/websocket/websocket.service';
import { CommentService } from './comment/comment.service';
import { Comment } from './comment/comment.model';
import { TranslateService } from '@ngx-translate/core';
import { StateService } from '../../shared/state/state.service';
import { find, sortBy } from 'lodash-es';
import * as moment from 'moment';
import { AppMessageService } from '../../shared/app-message/app-message.service';
import { SidebarPanelComponent } from '../../sidebar/sidebar-panel/sidebar-panel.component';
import { _ } from '../../shared/utils';
import { takeUntil, map, concatAll, catchError, delayWhen } from 'rxjs/operators';
import { LandingPageTile } from '../../shared/tiles/home-tile.model';
import { SidebarService } from '../../shared/sidebar/sidebar.service';
import { FlagService } from '../../shared/flag/flag.service';
import { IFlag } from '../../shared/flag/flag.model';

/**
 * Component for which contains crud to notification
 */
@Component({
  selector: 'qm-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class NotificationComponent extends SidebarPanelComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChildren('notification') notifications: QueryList<ElementRef>;
  /**
   * If train is selected
   */
  public trainMapObject: string;
  /**
   * Normal element
   */
  public normalElement: any;
  /**
   * Editing comment
   */
  public editCom: boolean;
  /**
   * Username
   */
  private userName: string;
  /**
   * Notification data
   */
  public formData: NotificationEntry;
  /**
   * Fetched notifications from database
   * @type {Array}
   */
  public notificationList: any = [];
  /**
   * New comment
   */
  public newComment: Comment;
  /**
   * Element details
   */
  public elementDetails: any;
  /**
   * Comments index
   */
  public activeCommentId: number;

  public flags: IFlag[] = [];

  public notificationIdPrefix: string = 'notification-';

  private scrollToNotification: boolean;
  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<void> = new Subject();

  private notificationUpdates: Subject<Observable<NotificationEntry[]>> = new Subject();
  private flagUpdates: Subject<Observable<IFlag[]>> = new Subject();

  /**
   * @param {ChangeDetectorRef} changeDetector
   * @param {AuthService} auth
   * @param {NotificationService} notificationService
   * @param {WebsocketService} websocket
   * @param {CommentService} commentService
   * @param {TranslateService} transalte
   */
  constructor(private changeDetector: ChangeDetectorRef,
              private notificationService: NotificationService,
              private auth: AuthService, private flagService: FlagService, private websocket: WebsocketService,
              private commentService: CommentService, private translate: TranslateService,
              private stateService: StateService, protected sidebarService: SidebarService,
              protected  appMessageService: AppMessageService) {
    super(appMessageService, sidebarService);
    this.componentName = NotificationComponent.getComponentName();
    this.userName = this.auth.getSubject();
    this.formData = null;
    this.newComment = null;
    this.editCom = false;
    this.loading = true;
    this.scrollToNotification = false;
  }

  public static initTile(tile: LandingPageTile) {
    tile.description = _('Notifications');
    tile.icon = 'far fa-sticky-note';
    tile.title = _('Notifications');
    tile.panel = NotificationComponent.getComponentName();
  }

  public static getComponentName(): string {
    return 'Notification';
  }

  /**
   * Angular OnInit hook, subscribe for map element changes
   */
  ngOnInit() {
    const activeId = this.notificationService.getActive();
    console.log('Init notification component');

    this.notificationUpdates.pipe(takeUntil(this.ngUnsubscribe), concatAll()).subscribe(data => {
      this.loading = false;
      this.notificationList = sortBy(data, [
        (o: NotificationEntry) => Math.max(
          ...[new Date(o.updatedAt).getTime()]
            .concat(o.comments.map(comment => new Date(comment.updatedAt).getTime()))
        )
      ]).reverse();
      this.checkFlags();
      this.changeDetector.markForCheck();
    });

    this.flagUpdates.pipe(takeUntil(this.ngUnsubscribe), concatAll()).subscribe(data => {
      this.flags = data;
      this.checkFlags();
      this.changeDetector.markForCheck();
    });

    this.websocket.newWebsocketMessage$.pipe(
      takeUntil(this.ngUnsubscribe),
      // to help prevent backend overload, stagger requests from different clients
      delayWhen(() => interval(Math.random() * 1000)),
      // extra takeUntil needed to ensure subscriptions don't trigger after the component's destruction; required for tests
      takeUntil(this.ngUnsubscribe)
    ).subscribe(data => {
      if (
        data.target === 'notification' ||
        data.target === 'notification_comment' ||
        (data.target === 'flag' && data.body && data.body.targetObjectType === 'notification')
      ) {
        this.prepareNotificationList();
      }
    });
    if (activeId) {
      this.scrollToNotification = true;
      this.newComment = new Comment();
    }
    this.prepareNotificationList();
  }
  ngAfterViewChecked() {
    let elemRef: ElementRef;
    if (this.notifications.length && this.scrollToNotification) {
      elemRef = this.notifications.toArray().find(ref => ref.nativeElement.dataset.id === this.notificationService.getActive().toString());
      console.log(elemRef);
      if (elemRef) {
        elemRef.nativeElement.scrollIntoView();
        this.scrollToNotification = false;
      }
    }
  }

  /**
   * Angular destructor function
   */
  ngOnDestroy() {
    this.notificationService.setActive(null);
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public clearSelection() {
    this.elementDetails = null;
    this.stateService.updateState('notification', null);
    this.prepareNotificationList();
    this.emitTitleChange(this.translate.instant(_('Notification center')));
  }

  /**
   * setActive sets activeId which is used to indentify notification from list where mouse is on top of it
   * @param {Notification} type
   */
  public setActive(notification: NotificationEntry) {
    if (!this.editCom && !this.activeCommentId) {
      if (this.notificationService.getActive() === notification.id) {
        this.notificationService.setActive(null);
      } else {
        this.notificationService.setActive(notification.id);
        this.newComment = new Comment();
      }
    }
    this.changeDetector.markForCheck();
  }

  /**
   * isActive return true/false depending which notification mouse is hovering on
   * @param {Notification} type
   */
  public isActive(notification: NotificationEntry) {
    if (notification.id === this.notificationService.getActive()) {
      return true;
    }
    return false;
  }

  /**
   * Updating for notificationList. Called when create, delete or edited notification.
   */
  private prepareNotificationList(): void {
    this.flagUpdates.next(this.flagService.listWithRetry());

    if (this.elementDetails) {
      let body: any;
      if (this.elementDetails.layer.source === 'trains') {
        if (find(this.elementDetails, 'TripIdentificationDate')) {
          body = {
            withDeleted: false,
            mapObjectId_like: 'vr_' + this.elementDetails.properties.tripNumber +
            '_' + this.elementDetails.properties.TripIdentificationDate
          };
          this.trainMapObject = body.mapObjectId_like;
        } else {
          body = {
            withDeleted: false,
            mapObjectId_like: 'vr_' + this.elementDetails.properties.tripNumber + '_%'
          };
          this.trainMapObject = 'vr_' + this.elementDetails.properties.tripNumber;
        }
      } else {
        body = {
          withDeleted: false,
          mapObjectId_like:
            this.elementDetails.layer.metadata.uniqueBy
              ? this.elementDetails.properties[this.elementDetails.layer.metadata.uniqueBy]
              : this.elementDetails.id
        };
      }
      this.notificationUpdates.next(
        this.notificationService.filterWithRetry(body).pipe(
          catchError(() => EMPTY)
        )
      );
    } else {
      this.notificationUpdates.next(
        this.notificationService.listWithRetry().pipe(
          map((data) => {
            if (data) {
              for (const notification of data) {
                if (Math.abs(moment(notification.createdAt).diff(new Date(), 'm')) < 16) {
                  notification.state = 'new';
                } else if (Math.abs(moment(notification.updatedAt).diff(new Date(), 'm')) < 16) {
                  notification.state = 'updated';
                }
              }
            }
            return data;
          }),
          catchError(() => EMPTY)
        )
      );
    }
  }

  /**
   * Selected comment is being used to get right comment
   * @param {any} comment
   */
  public editComment(comment: Comment) {
    this.newComment = comment;
    this.activeCommentId = comment.id;
    this.editCom = true;
  }

  /**
   * Prepares showForm array and startDate
   */
  public addNewNotification() {
    this.formData = new NotificationEntry();
    this.formData.userId = this.userName;
    this.formData.comments = [];
    this.formData.status = null;
  }

  /**
   * Prepares formData inserting original values to it
   * @param data
   */
  public editNotification(data: NotificationEntry) {
    this.formData = data;
  }

  /**
   * Removes notification from database. Doesn't delete it fully.
   * @param data {Notification}
   */
  public async deleteNotification(notification: NotificationEntry) {
    await this.notificationService.remove(notification);
    this.prepareNotificationList();
  }

  /**
   * Function for saving the new or edited data to database
   * After save updates the notificationList
   * @param data
   * @param {Notification} type
   */
  public async save(notification: NotificationEntry) {
    if (this.trainMapObject) {
      notification.mapObjectId = this.trainMapObject;
    } else if (this.normalElement) {
      notification.mapObjectId = this.normalElement;
    }
    if (!notification.id) {
      notification.comments = [];
      await this.notificationService.create(notification);
      this.prepareNotificationList();
    } else {
      await this.notificationService.edit(notification);
      this.prepareNotificationList();
    }
    this.cancel();
  }

  /**
   * Save new Comment
   * @param {Comment} type
   * @param {Notification} type
   */
  public async saveComment(comment: Comment, notification: NotificationEntry) {
    if (!comment.userId && !comment.notificationId) {
      comment.userId = this.userName;
      comment.notificationId = notification.id;
    }
    if (!comment.id) {
      await this.commentService.create(comment);
      this.prepareNotificationList();
    } else {
      await this.commentService.edit(comment);
      this.prepareNotificationList();
    }
    this.activeCommentId = null;
    this.editCom = false;
    this.cancel();
  }

  /**
   * Delete comment
   * @param {Comment} type
   */
  public async deleteComment(comment: Comment) {
    await this.commentService.remove(comment);
    this.prepareNotificationList();
  }

  getColor(a: string) {
    const time = moment(a).fromNow(true);
    const test = /(?:hour)|(?:hours)|(?:minutes)|(?:second)/;
    if (test.test(time)) {
      return '2px solid #6DAC43';
    }
  }
  /**
   * If create/edit notification is being cancelled.
   */
  private cancel() {
    this.formData = null;
    if (!this.editCom && !this.activeCommentId) {
      this.newComment = new Comment();
      this.activeCommentId = null;
      this.editCom = false;
      this.changeDetector.markForCheck();
    }
  }

  public async flagNotification(notification: NotificationEntry) {
    if (!this.isFlagged(notification)) {
      const flag: IFlag = new IFlag();
      flag.targetObjectId = notification.id.toString();
      flag.targetObjectType = 'notification';
      flag.global = false;
      flag.userId = notification.userId.toString() ? notification.userId.toString() : '';
      await this.flagService.create(flag);
    } else {
      await this.flagService.remove(find(this.flags, (o: any) => o.targetObjectId === notification.id.toString()));
    }
    this.prepareNotificationList();
  }

  isFlagged(notification: NotificationEntry) {
    if (notification.id && find(this.flags, (o: any) => o.targetObjectId === notification.id.toString())) {
      return true;
    }
    return false;
  }

  checkFlags() {
    if (this.notificationList && this.notificationList.length > 0) {
      this.notificationList.sort((o: any) => {
        if (o.id && find(this.flags, (k: any) => k.targetObjectId === o.id.toString())) {
          return -1;
        }
        return 0;
      });
    }
  }
}
